import { type InputProps as MuiInputProps, TextField } from '@mui/material';
import { Field, FieldProps, useFormikContext } from 'formik';
import { camelCase, get } from 'lodash';
import React, { ChangeEvent, useCallback, useMemo } from 'react';

import { InputComponent } from '..';
import { normalizeSize } from '../helpers';

export interface InputTextProps extends InputComponent {
  type?: HTMLInputElement['type'];
  min?: number | string;
  max?: number | string;
  step?: number | 'any';
  onChange?: (name: string, value: number | string) => void;
  InputProps?: Partial<MuiInputProps>;
  /**
   * Keep `required` validation but hide the label's `*` asterisk. Useful where
   * the field is validated but the design doesn't call for a required marker.
   */
  hideRequiredAsterisk?: boolean;
}

export function InputText({
  label,
  disabled,
  hidden,
  required,
  type = 'text',
  className,
  hint,
  nameOverride,
  min = undefined,
  max = undefined,
  placeholder,
  size: propSize = 'medium',
  onChange = undefined,
  step = 1,
  showLabel = !hidden,
  readOnly = false,
  InputProps = undefined,
  hideRequiredAsterisk = false,
}: InputTextProps) {
  const { errors, handleChange } = useFormikContext<Record<string, string>>();

  const validate = useCallback(
    (value: string | number | undefined) => {
      if (type === 'number') {
        // The form may store the value as a string (Formik's default for
        // controlled inputs) or as a coerced number. Reject only when truly
        // empty or non-numeric — lodash `isFinite('50')` is false, which used
        // to flag every filled string-number input as "required".
        if (required) {
          if (value === undefined || value === null || value === '') {
            return `${label} is required.`;
          }
          const numeric = typeof value === 'number' ? value : Number(value);
          if (!Number.isFinite(numeric)) {
            return `${label} is required.`;
          }
        }
      } else if (required && (!value || value === undefined)) {
        return `${label} is required.`;
      }
      return undefined;
    },
    [label, required, type],
  );

  const name = useMemo(
    () => nameOverride ?? camelCase(label),
    [label, nameOverride],
  );

  const inputPlaceholder = useMemo(
    () => placeholder ?? label?.replace(/[?:]/g, ''),
    [placeholder, label],
  );

  const size = normalizeSize(propSize);

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onChange?.(name, e.target.value);
      handleChange(e);
    },
    [name, onChange, handleChange],
  );

  const handleWheel = useCallback(
    (e: React.WheelEvent<HTMLInputElement>) => {
      if (type === 'number') {
        e.currentTarget?.blur();
      }
    },
    [type],
  );

  const error = useMemo(() => get(errors, name), [errors, name]);

  return (
    <Field name={name} validate={validate} isInvalid={!!error}>
      {({ field }: FieldProps) => (
        <TextField
          onBlur={field.onBlur}
          name={name}
          fullWidth
          variant="outlined"
          // Pass `undefined` (not `false`) when the label is hidden. MUI only
          // skips rendering the <InputLabel> when `label == null`; a boolean
          // `false` still renders an empty label, and with `required` that
          // empty label shows a lone `*` floating in the middle of the field.
          label={showLabel ? label : undefined}
          className={className}
          disabled={disabled}
          hidden={hidden}
          required={required && !readOnly && !hideRequiredAsterisk}
          placeholder={inputPlaceholder}
          slotProps={{
            inputLabel: { shrink: !!(showLabel && label) },
            input: {
              ...InputProps,
              inputProps: {
                min,
                max,
                step,
                readOnly,
                ...(type === 'number' && { onWheel: handleWheel }),
              } as React.HTMLAttributes<HTMLInputElement>,
            },
          }}
          type={type}
          size={size === 'large' ? 'medium' : size}
          onChange={handleInputChange}
          value={field.value ?? ''}
          error={!!error}
          helperText={error || hint}
        />
      )}
    </Field>
  );
}
